import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CodeIcon, GitMergeIcon, BugIcon, RefreshCwIcon, LayersIcon } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';
const pieData = [{
  name: 'AI-Generated',
  value: 38,
  color: '#00D4FF'
}, {
  name: 'Human',
  value: 62,
  color: '#1E2D45'
}];
const prSizeData = [{
  type: 'AI PRs',
  size: 340
}, {
  type: 'Human PRs',
  size: 520
}, {
  type: 'Mixed PRs',
  size: 430
}];
const bugCorrelationData = [{
  week: 'W1',
  ai: 3,
  human: 5
}, {
  week: 'W2',
  ai: 2,
  human: 6
}, {
  week: 'W3',
  ai: 4,
  human: 4
}, {
  week: 'W4',
  ai: 2,
  human: 7
}, {
  week: 'W5',
  ai: 1,
  human: 5
}, {
  week: 'W6',
  ai: 3,
  human: 6
}];
const stats = [{
  icon: CodeIcon,
  label: '% AI-Generated Code',
  sublabel: 'per team average',
  value: '38%',
  color: '#00D4FF',
  detail: 'Up from 24% last quarter'
}, {
  icon: LayersIcon,
  label: 'Diff Volume by AI',
  sublabel: 'lines changed per week',
  value: '12.4K',
  color: '#3B82F6',
  detail: '+340% vs 6 months ago'
}, {
  icon: GitMergeIcon,
  label: 'Avg AI PR Size',
  sublabel: 'lines of code',
  value: '340',
  color: '#A855F7',
  detail: '35% smaller than human PRs'
}, {
  icon: BugIcon,
  label: 'Bug Correlation',
  sublabel: 'AI-generated commits',
  value: '0.8x',
  color: '#10B981',
  detail: 'Lower than human baseline'
}, {
  icon: RefreshCwIcon,
  label: 'Net-New vs Refactor',
  sublabel: 'AI code classification',
  value: '71/29',
  color: '#F59E0B',
  detail: '71% net-new, 29% refactor'
}];
export function CodeIntelligenceSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-24 px-6 bg-[#080C14] relative overflow-hidden" data-id="element-141">
      {/* Subtle background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-5" style={{
      background: 'radial-gradient(circle, #00D4FF 0%, transparent 70%)'
    }} data-id="element-142" />

      <div className="max-w-6xl mx-auto relative" data-id="element-143">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-4" data-id="element-144">
          <span className="text-xs font-bold tracking-[0.25em] text-cyan-400 uppercase" data-id="element-145">
            Deep Code Contribution Intelligence
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.1,
        duration: 0.6
      }} className="mb-12" data-id="element-146">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-3" data-id="element-147">
            You Don't Just See More PRs.
            <br data-id="element-148" />
            <span className="text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(135deg, #00D4FF 0%, #3B82F6 100%)'
          }} data-id="element-149">
              You See Who and What Created Them.
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl" data-id="element-150">
            Deep attribution across every commit, diff, and pull request —
            classified by agent, human, or collaboration.
          </p>
        </motion.div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-id="element-151">
          {stats.map((stat, i) => {
          const Icon = stat.icon;
          return <motion.div key={stat.label} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            delay: i * 0.1,
            duration: 0.5
          }} className="bg-[#0D1421] border border-[#1E2D45] rounded-xl p-5" data-id="element-152">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{
              backgroundColor: `${stat.color}15`
            }} data-id="element-153">
                  <Icon className="w-4 h-4" style={{
                color: stat.color
              }} data-id="element-154" />
                </div>
                <div className="text-2xl font-black text-white mb-1" data-id="element-155">
                  {stat.value}
                </div>
                <div className="text-xs font-semibold text-gray-300 mb-0.5" data-id="element-156">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-600 mb-3" data-id="element-157">
                  {stat.sublabel}
                </div>
                <div className="text-xs" style={{
              color: stat.color
            }} data-id="element-158">
                  {stat.detail}
                </div>
              </motion.div>;
        })}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-159">
          {/* Pie chart */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.5,
          duration: 0.6
        }} className="bg-[#0D1421] border border-[#1E2D45] rounded-xl p-5" data-id="element-160">
            <div className="text-white font-semibold text-sm mb-1" data-id="element-161">
              Code Authorship
            </div>
            <div className="text-gray-500 text-xs mb-4" data-id="element-162">
              AI vs Human contribution
            </div>
            <div className="h-36 flex items-center justify-center" data-id="element-163">
              <ResponsiveContainer width="100%" height="100%" data-id="element-164">
                <PieChart data-id="element-165">
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={3} dataKey="value" data-id="element-166">
                    {pieData.map((entry, index) => <Cell key={index} fill={entry.color} data-id="element-167" />)}
                  </Pie>
                  <Tooltip contentStyle={{
                  backgroundColor: '#0D1421',
                  border: '1px solid #1E2D45',
                  borderRadius: 8
                }} formatter={(v: number) => [`${v}%`]} data-id="element-168" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2" data-id="element-169">
              {pieData.map(d => <div key={d.name} className="flex items-center gap-1.5" data-id="element-170">
                  <span className="w-2 h-2 rounded-full" style={{
                backgroundColor: d.color
              }} data-id="element-171" />
                  <span className="text-xs text-gray-400" data-id="element-172">
                    {d.name} {d.value}%
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* PR Size comparison */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.6,
          duration: 0.6
        }} className="bg-[#0D1421] border border-[#1E2D45] rounded-xl p-5" data-id="element-173">
            <div className="text-white font-semibold text-sm mb-1" data-id="element-174">
              Avg PR Size
            </div>
            <div className="text-gray-500 text-xs mb-4" data-id="element-175">
              Lines of code by PR type
            </div>
            <div className="h-36" data-id="element-176">
              <ResponsiveContainer width="100%" height="100%" data-id="element-177">
                <BarChart data={prSizeData} barSize={28} data-id="element-178">
                  <XAxis dataKey="type" tick={{
                  fill: '#6B7280',
                  fontSize: 10
                }} axisLine={false} tickLine={false} data-id="element-179" />
                  <YAxis hide data-id="element-180" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#0D1421',
                  border: '1px solid #1E2D45',
                  borderRadius: 8
                }} formatter={(v: number) => [`${v} lines`]} data-id="element-181" />
                  <Bar dataKey="size" radius={[4, 4, 0, 0]} data-id="element-182">
                    <Cell fill="#00D4FF" data-id="element-183" />
                    <Cell fill="#3B82F6" data-id="element-184" />
                    <Cell fill="#A855F7" data-id="element-185" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Bug correlation */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.7,
          duration: 0.6
        }} className="bg-[#0D1421] border border-[#1E2D45] rounded-xl p-5" data-id="element-186">
            <div className="text-white font-semibold text-sm mb-1" data-id="element-187">
              Bug Correlation
            </div>
            <div className="text-gray-500 text-xs mb-4" data-id="element-188">
              AI vs Human commits (bugs/week)
            </div>
            <div className="h-36" data-id="element-189">
              <ResponsiveContainer width="100%" height="100%" data-id="element-190">
                <BarChart data={bugCorrelationData} barSize={10} barGap={2} data-id="element-191">
                  <XAxis dataKey="week" tick={{
                  fill: '#6B7280',
                  fontSize: 10
                }} axisLine={false} tickLine={false} data-id="element-192" />
                  <YAxis hide data-id="element-193" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#0D1421',
                  border: '1px solid #1E2D45',
                  borderRadius: 8
                }} formatter={(v: number, name: string) => [v, name === 'ai' ? 'AI Commits' : 'Human Commits']} data-id="element-194" />
                  <Bar dataKey="ai" fill="#00D4FF" radius={[2, 2, 0, 0]} data-id="element-195" />
                  <Bar dataKey="human" fill="#3B82F6" radius={[2, 2, 0, 0]} opacity={0.5} data-id="element-196" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-3 mt-1" data-id="element-197">
              <div className="flex items-center gap-1" data-id="element-198">
                <span className="w-2 h-2 rounded-sm bg-cyan-400" data-id="element-199" />
                <span className="text-xs text-gray-500" data-id="element-200">AI</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-201">
                <span className="w-2 h-2 rounded-sm bg-blue-500 opacity-50" data-id="element-202" />
                <span className="text-xs text-gray-500" data-id="element-203">Human</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}